import { useQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo } from "react";

import {
  GetEvidencesCountDocument,
  type GetEvidencesCountQuery,
  type GetEvidencesCountQueryVariables,
} from "~/features/evidences/gqls";
import type { EvidenceListFilters } from "~/features/evidences/types";
import { buildEvidencesCountVariables } from "~/features/evidences/utils/transformers";
import { useTenant } from "~/features/tenant/context";

const EVIDENCES_COUNT_TTL_MS = 60 * 60 * 1000;
// The aggregate is expensive; allowing an hour of staleness keeps the list responsive.
const evidencesCountFetchedAt = new Map<string, number>();

interface UseEvidenceCountArgs {
  filters: EvidenceListFilters;
}

interface UseEvidenceCountResult {
  count: number | undefined;
  refresh: () => Promise<unknown>;
}

export function useEvidenceCount({ filters }: UseEvidenceCountArgs): UseEvidenceCountResult {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const variables = useMemo<GetEvidencesCountQueryVariables>(
    () => buildEvidencesCountVariables({ tenantId, filters }),
    [filters, tenantId],
  );
  const variablesKey = JSON.stringify(variables);
  const isFresh =
    // The TTL must be evaluated during render to select Apollo's initial fetch policy.
    // eslint-disable-next-line react-hooks/purity
    Date.now() - (evidencesCountFetchedAt.get(variablesKey) ?? 0) < EVIDENCES_COUNT_TTL_MS;
  const { data, error, refetch } = useQuery<GetEvidencesCountQuery, GetEvidencesCountQueryVariables>(
    GetEvidencesCountDocument,
    {
      variables,
      fetchPolicy: isFresh ? "cache-first" : "network-only",
    },
  );

  useEffect(() => {
    if (data) evidencesCountFetchedAt.set(variablesKey, Date.now());
  }, [data, variablesKey]);

  useEffect(() => {
    if (error) Sentry.captureException(error);
  }, [error]);

  const refresh = useCallback(async () => {
    const result = await refetch();
    evidencesCountFetchedAt.set(variablesKey, Date.now());
    return result;
  }, [refetch, variablesKey]);

  return {
    count: data?.evidencesAggregate.aggregate?.totalCount,
    refresh,
  };
}
